import {
  isCommandStep,
  isDefaultCommandStep,
  isMatrixCommandStep,
  isParallelCommandStep,
  isGroupStep,
  Step,
  CommandStep,
  isTriggerStep,
} from "~/lib/pipeline";
import { twMerge } from "tailwind-merge";
import { StepLink } from "~/components/build/Show/components/Sidebar/TreeNode/StepLink";
import { Label } from "~/components/build/Show/components/Sidebar/TreeNode/Label";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import Emojify from "~/components/shared/Emojify";
import {
  useMostRecentJob,
  useBuild,
  useMostRecentMatrixJobsForStep,
  useMostRecentParallelJobsForStep,
} from "~/components/build/Show/lib/BuildContext";
import CommandStepLabel from "~/components/build/Show/components/CommandStepLabel";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { GroupStepCount } from "~/components/build/Show/components/GroupStepCount";
import { JobCount, StatsJobCount } from "~/components/build/Show/components/JobCount";
import { Build, CommandJob } from "~/components/build/Show/lib/types";
import { ReactNode } from "react";
import Duration from "~/components/shared/Duration";
import { StepRetries } from "~/components/build/Show/components/Sidebar/TreeNode/JobRetries";
import { RunDuration } from "~/components/job/Job/JobDuration";

interface Props {
  step: Step;
  onClick?: () => void;
  className?: string;
  active?: boolean;
  failed?: boolean;
  depth?: number;
}

const Count = (step: Step, build: Build) => {
  if (Features.ProgressiveBuildShow && (isMatrixCommandStep(step) || isParallelCommandStep(step))) {
    return <StatsJobCount step={step} />;
  }

  if (isGroupStep(step)) {
    return <GroupStepCount step={step} />;
  } else if (isMatrixCommandStep(step)) {
    return CountForMatrixStep(build, step);
  } else if (isParallelCommandStep(step)) {
    return CountForParallelStep(build, step);
  }
  return null;
};

const CountForMatrixStep = (build: Build, step: CommandStep) => {
  return <JobCount jobs={useMostRecentMatrixJobsForStep(step.uuid)} step={step} />;
};

const CountForParallelStep = (build: Build, step: CommandStep) => {
  return <JobCount jobs={useMostRecentParallelJobsForStep(step.uuid)} step={step} />;
};

/**
 * A catch-all step list item.
 */
export function StepItem({ step, onClick, className, active, failed, depth = 0 }: Props) {
  const job = useMostRecentJob(step.uuid);
  const waitingOnConcurrencyGroup = isDefaultCommandStep(step) && job?.state === "limited";
  const waitingOnPlatformLimit = isDefaultCommandStep(step) && job?.state === "platform_limited";
  const { build } = useBuild();

  let label: ReactNode;
  if (isCommandStep(step)) {
    label = <CommandStepLabel {...step} />;
  } else {
    // Add a non-breaking space to prevent empty label from collapsing
    label = <Emojify text={step.label} className="empty:before:content-['\00a0']" />;
  }

  let duration: ReactNode | undefined;
  if (isDefaultCommandStep(step)) {
    if (Features.ProgressiveBuildShow && step.statistics?.latestJobStartedAt) {
      duration = (
        <Duration.Short
          from={step.statistics.latestJobStartedAt}
          to={step.statistics.latestJobFinishedAt || undefined}
        />
      );
    } else {
      if (job?.type === "script" && job.startedAt) {
        duration = <RunDuration job={job as CommandJob} />;
      }
    }
  }

  return (
    <StepLink
      state={step.state}
      onClick={onClick}
      className={className}
      type={step.type}
      to={
        // Skip opening the drawer when clicking on a parallel or matrix **step** (not job) in the sidebar since
        // jobs are rendered below when the step is expanded.
        isParallelCommandStep(step) || isMatrixCommandStep(step) || isGroupStep(step)
          ? resolveStepUrl(step.uuid, { open: false })
          : resolveStepUrl(step.uuid)
      }
      active={active}
      failed={failed}
      depth={depth}
    >
      <Label
        info={isDefaultCommandStep(step) && <StepRetries step={step} />}
        duration={duration}
        status={
          <StateIcon
            className={twMerge(
              "-mr-0.5",
              textColorForStep({
                ...step,
                waitingOnConcurrencyGroup,
                waitingOnPlatformLimit,
              }),
            )}
            waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
            waitingOnPlatformLimit={waitingOnPlatformLimit}
            {...step}
          />
        }
        count={Count(step, build)}
      >
        {label}
      </Label>
    </StepLink>
  );
}
